import React, { useEffect, useState } from 'react';
import { fetchSchoolDisplaySettings, updateSchoolDisplaySettings } from '../../services/api';

const toggleRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '14px 16px',
  border: '1px solid var(--ae-border)',
  borderRadius: 14,
  background: 'var(--ae-surface)',
};

function ToggleButton({ label, checked, onClick, disabled }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={checked}
      aria-label={label}
      style={{
        width: 52,
        height: 28,
        borderRadius: 999,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        background: checked ? 'var(--ae-primary)' : 'var(--ae-border)',
        position: 'relative',
        transition: 'background 0.15s',
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 27 : 3,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: '#ffffff',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.3)',
          transition: 'left 0.15s',
        }}
      />
    </button>
  );
}

export default function FamilyDisplaySettingsPanel({ schoolId, schoolName }: any) {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [savingKey, setSavingKey] = useState<any>(null);
  const [error, setError] = useState<any>('');
  const hasValidSchoolId = Number.isInteger(Number(schoolId)) && Number(schoolId) > 0;

  useEffect(() => {
    if (!hasValidSchoolId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setSettings(null);
    setLoading(true);
    setError('');
    fetchSchoolDisplaySettings(schoolId)
      .then((data) => {
        if (cancelled) return;
        setSettings({
          hide_percent_for_family_student: !!data.hide_percent_for_family_student,
          gray_out_red_for_family_student: !!data.gray_out_red_for_family_student,
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load display settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId, hasValidSchoolId]);

  const toggle = async (key) => {
    const nextValue = !settings[key];
    setSavingKey(key);
    setError('');
    try {
      const data = await updateSchoolDisplaySettings(schoolId, { [key]: nextValue });
      setSettings({
        hide_percent_for_family_student: !!data.hide_percent_for_family_student,
        gray_out_red_for_family_student: !!data.gray_out_red_for_family_student,
      });
    } catch (e: any) {
      setError(e.message || 'Failed to update display setting.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div style={{ border: '1px solid var(--ae-border)', borderRadius: 16, padding: 16, background: 'var(--ae-surface-soft)' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 4 }}>
        Family &amp; Student Progress View{schoolName ? ` \u2014 ${schoolName}` : ''}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ae-text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        Only Superadmin can change these school preferences for family and student accounts. Teachers and leadership always see full detail.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ae-text-muted)' }}>Loading...</div>
      ) : !hasValidSchoolId ? (
        <div style={{ fontSize: 13, color: 'var(--ae-text-muted)' }}>School ID unavailable for this profile.</div>
      ) : settings ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={toggleRow}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ae-text)' }}>Hide mastery percentages</div>
              <div style={{ fontSize: 12, color: 'var(--ae-text-muted)', marginTop: 2 }}>Hides "% mastered" figures and progress bars for family and student accounts.</div>
            </div>
            <ToggleButton
              label="Hide mastery percentages"
              checked={settings.hide_percent_for_family_student}
              disabled={savingKey !== null}
              onClick={() => toggle('hide_percent_for_family_student')}
            />
          </div>

          <div style={toggleRow}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ae-text)' }}>Show "Not Yet" as gray instead of red</div>
              <div style={{ fontSize: 12, color: 'var(--ae-text-muted)', marginTop: 2 }}>Replaces the red "Not Yet" color with a neutral gray for family and student accounts.</div>
            </div>
            <ToggleButton
              label='Show "Not Yet" as gray instead of red'
              checked={settings.gray_out_red_for_family_student}
              disabled={savingKey !== null}
              onClick={() => toggle('gray_out_red_for_family_student')}
            />
          </div>
        </div>
      ) : null}

      {error && <div role="alert" style={{ fontSize: 12, color: 'var(--brand-red)', marginTop: 10 }}>{error}</div>}
    </div>
  );
}
